import { Grid, Stack, Box } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useMemo } from 'react';
import { ITeamMembersResponse, getAllTeamMemberRecordsOfTeam } from 'wisestep-api';
import {
  WsTypography,
  CardVariant,
  EmptyTeamsAndUsersIcon,
  Item,
  WsCard,
  WsTypoVariants,
  WsColors,
  WsTypoColors,
} from 'wisestep-base';

import PersonAvatar from '../../../images/Avatar/Person.svg';
import { getIdByEmail } from '../../candidates/utils/CandidateHelper';
import { StyledPaginationBox } from '../../clientsSettings/components/OrganizationalUnitLayoutNew';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StatusTypography } from '../../users/components/TotalInvitesList';
import { teamFilters } from '../helper/defaultValues';

import { StyledDataGrid } from './TeamListNew';
import WsAvatar from './WsAvatar';

interface ITeamsUsersTab {
  teamId?: string;
}

const TeamsUsersTab = ({ teamId }: ITeamsUsersTab) => {
  const [teamData, setTeamData] = useState<{
    totalUsersCount: number;
    teamMembers: ITeamMembersResponse[];
  }>({ totalUsersCount: 0, teamMembers: [] });
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  useEffect(() => {
    const getMembersOfTeam = async () => {
      if (teamId) {
        const response = await getAllTeamMemberRecordsOfTeam(teamId, {
          ...teamFilters,
          pageSize: pageSize,
          pageNumber: pageNo,
        });
        if (response.data) setTeamData(response.data);
      }
    };
    getMembersOfTeam();
  }, [pageNo, pageSize, teamId]);

  const data = useMemo(
    () =>
      teamData.teamMembers?.map((user) => ({
        id: user.memberEmail,
        name: user.name,
        role: user.role,
        roles: user.roles,
        teamId: user.teamId,
        memberEmail: user.memberEmail,
        status: user.status,
      })) ?? [],
    [teamData.teamMembers]
  );

  const columns: GridColumns = [
    {
      headerName: 'Name',
      field: 'name',
      flex: 1,
      renderCell: (param) => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <WsAvatar
              src={PersonAvatar}
              active={param.row.status == 'Activated'}
              size="small"
              variant="circular"
            />
          </Grid>
          <Grid item>
            <WsTypography
              color={WsTypoColors.PRIMARY750}
              onClick={() => getIdByEmail(param.row.memberEmail)}
              cursor="pointer"
            >
              {param.value}
            </WsTypography>
          </Grid>
        </Grid>
      ),
    },
    {
      headerName: 'Email',
      field: 'memberEmail',
      flex: 1,
      renderCell: (param) => <WsTypography>{param.value}</WsTypography>,
    },
    {
      headerName: 'Role',
      field: 'role',
      flex: 1,
      renderCell: (param) => <WsTypography>{param.value}</WsTypography>,
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      renderCell: (param) => (
        <StatusTypography cell={param?.value}>{param?.value}</StatusTypography>
      ),
    },
  ];

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {teamData.totalUsersCount}
        </WsTypography>
      </Box>
      {teamData.totalUsersCount > 10 && (
        <WsDataGridPagination
          count={Math.ceil(teamData.totalUsersCount / pageSize)}
          page={pageNo}
          onPageChange={(_$, newPage) => setPageNo(newPage)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid item xs={12} mt={2} p={2} bgcolor={WsColors.White}>
      {isEmpty(teamData.teamMembers) ? (
        <WsCard
          isButton={false}
          cardType={CardVariant.WITH_LIGHT_BG}
          middleComponent={
            <Item>
              <Stack alignItems="center" spacing={3}>
                <EmptyTeamsAndUsersIcon />
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                  No user tagged to this Team.
                </WsTypography>
              </Stack>
            </Item>
          }
        />
      ) : (
        <>
          {paginationComponent}
          <StyledDataGrid autoHeight hideFooter columns={columns} rows={data ?? []} />
          {teamData.totalUsersCount > 10 && paginationComponent}
        </>
      )}
    </Grid>
  );
};

export default TeamsUsersTab;
